import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Button,
  Autocomplete,
  TextField,
  IconButton,
  Tooltip,
  useMediaQuery,
  Typography,
  Snackbar,
  Alert,
  useTheme,
  InputAdornment,
  Paper,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import { useParams } from "react-router-dom";
import { useSpring, animated } from "react-spring";
import { useDrag, usePinch } from "@use-gesture/react";
import OmcorrSVG from "./FloorPlan/OmcorrSVG";
import {
  Add,
  Remove,
  GpsFixed,
  LocationOn,
  Flag,
  Search,
} from "@mui/icons-material";

const SVGViewer = React.memo(
  React.forwardRef(
    (
      {
        scale,
        setScale,
        offset,
        setOffset,
        fromLabel,
        toLabel,
        containerRef,
        stalls,
      },
      ref
    ) => {
      const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
      const isMobile = useMediaQuery("(max-width:600px)");
      const svgRef = useRef(null);
      const [isDragging, setIsDragging] = useState(false);
      const [svgReady, setSvgReady] = useState(false);

      const setRefs = useCallback(
        (node) => {
          if (typeof ref === "function") ref(node);
          else if (ref) ref.current = node;

          if (node) {
            // Check if the node itself is an SVG element
            if (node instanceof SVGElement) {
              svgRef.current = node;
              setSvgReady(true);
              const viewBox = node.getAttribute("viewBox");
              if (viewBox) {
                const [, , vbWidth, vbHeight] = viewBox
                  .split(/\s+|,/)
                  .map(Number);
                setDimensions({ width: vbWidth, height: vbHeight });
              }
            } else {
              // Otherwise, look for SVG element inside the node
              const svgElement = node.querySelector("svg");
              if (svgElement) {
                svgRef.current = svgElement;
                setSvgReady(true);
                const viewBox = svgElement.getAttribute("viewBox");
                if (viewBox) {
                  const [, , vbWidth, vbHeight] = viewBox
                    .split(/\s+|,/)
                    .map(Number);
                  setDimensions({ width: vbWidth, height: vbHeight });
                }
              } else {
                // Set up MutationObserver if SVG isn't immediately available
                const observer = new MutationObserver(() => {
                  const svg = node.querySelector("svg");
                  if (svg) {
                    svgRef.current = svg;
                    setSvgReady(true);
                    const viewBox = svg.getAttribute("viewBox");
                    if (viewBox) {
                      const [, , vbWidth, vbHeight] = viewBox
                        .split(/\s+|,/)
                        .map(Number);
                      setDimensions({ width: vbWidth, height: vbHeight });
                    }
                    observer.disconnect();
                  }
                });
                observer.observe(node, { childList: true, subtree: true });
              }
            }
          }
        },
        [ref]
      );

      // Setup spring animation for smooth movement
      const [spring, api] = useSpring(() => ({
        xy: offset,
        config: { mass: 0.8, tension: 320, friction: 40 },
      }));

      useEffect(() => {
        api.start({ xy: offset });
      }, [offset, api]);

      // Use drag gesture for panning
      useDrag(
        ({ movement: [mx, my], first, last, active }) => {
          if (first) {
            setIsDragging(true);
            api.start({ xy: offset, immediate: true });
          }

          if (active) {
            const newOffset = [offset[0] + mx, offset[1] + my];
            api.start({ xy: newOffset, immediate: true });
          }

          if (last) {
            setIsDragging(false);
            setOffset([offset[0] + mx, offset[1] + my]);
          }
        },
        {
          target: containerRef,
          filterTaps: true,
          rubberband: true,
        }
      );

      // Use pinch gesture for zooming
      usePinch(
        ({ offset: [d], first, last }) => {
          if (first) {
            // Store the position under the pinch for better scaling
            if (svgRef.current) {
              svgRef.current.pinchPoint = { x: 0, y: 0 };
            }
          }

          // Calculate new scale with smoother scaling
          const newScale = Math.max(0.5, Math.min(5, 1 + d * 0.008));

          setScale(newScale);
        },
        {
          target: containerRef,
          scaleBounds: { min: 0.5, max: 5 },
          rubberband: true,
        }
      );

      useEffect(() => {
        if (!svgReady) return;

        const highlightLabel = (labelId, isFrom) => {
          try {
            if (!labelId) return;

            // Find the stall data to get the transform coordinates
            const stall = stalls.find((s) => s.id === labelId);
            if (!stall) {
              console.warn(`Stall not found for: ${labelId}`);
              return;
            }

            // Extract coordinates from the transform string
            const transformMatch = stall.transform.match(
              /translate\(([\d.]+)[,\s]+([\d.]+)\)/
            );
            if (!transformMatch || transformMatch.length < 3) {
              console.warn("Invalid transform format:", stall.transform);
              return;
            }

            // Get precise coordinates from transform
            const [_, tx, ty] = transformMatch;
            const translateX = parseFloat(tx);
            const translateY = parseFloat(ty);

            // Calculate final coordinates (no additional offsets needed)
            const finalX = translateX;
            const finalY = translateY;

            // 8. Create circle with exact transform values
            const ns = "http://www.w3.org/2000/svg";
            const sanitizedLabel = labelId.replace(/ /g, "-");
            const circleId = `highlight-${sanitizedLabel}-${
              isFrom ? "from" : "to"
            }`;

            let circle =
              svgRef.current.querySelector(`#${CSS.escape(circleId)}`) ||
              document.createElementNS(ns, "circle");

            // Updated circle styling with more vibrant colors
            const fromColors = {
              fill: "rgba(128, 0, 255, 0.25)",
              stroke: "#8000ff",
              glow: "0 0 10px rgba(128, 0, 255, 0.8)",
            };

            const toColors = {
              fill: "rgba(255, 107, 185, 0.25)",
              stroke: "#ff57b6",
              glow: "0 0 10px rgba(255, 87, 182, 0.8)",
            };

            circle.setAttribute("id", circleId);
            circle.setAttribute("cx", finalX + 30);
            circle.setAttribute("cy", finalY);
            circle.setAttribute("r", "51.315625");
            circle.setAttribute(
              "fill",
              isFrom ? fromColors.fill : toColors.fill
            );
            circle.setAttribute(
              "stroke",
              isFrom ? fromColors.stroke : toColors.stroke
            );
            circle.setAttribute("stroke-width", "3");
            circle.setAttribute("stroke-dasharray", "4,2");
            circle.style.pointerEvents = "none";
            // Add glow effect with filter
            circle.setAttribute(
              "filter",
              `drop-shadow(${isFrom ? fromColors.glow : toColors.glow})`
            );

            if (!circle.parentNode) {
              svgRef.current.appendChild(circle);
            }
          } catch (error) {
            console.error("Error highlighting label:", error);
          }
        };

        // Clear previous highlights
        const circles = svgRef.current.querySelectorAll('[id^="highlight-"]');
        circles.forEach((circle) => circle.remove());

        // Apply new highlights
        highlightLabel(fromLabel, true);
        highlightLabel(toLabel, false);

        return () => {
          // Remove highlight circles
          const circles = svgRef.current.querySelectorAll('[id^="highlight-"]');
          circles.forEach((circle) => circle.remove());
        };
      }, [svgReady, fromLabel, toLabel]);

      return (
        <animated.div
          style={{
            transform: spring.xy.to(
              (x, y) => `translate3d(${x}px, ${y}px, 0) scale(${scale})`
            ),
            transformOrigin: "0 0",
            cursor: isDragging ? "grabbing" : "grab",
            touchAction: "none",
          }}
        >
          <Box
            ref={setRefs}
            sx={{
              width: "100%",
              height: "auto",
              filter: isMobile ? "contrast(1.1)" : "none",
              transition: isDragging
                ? "none"
                : "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              transform: `scale(${scale})`,
              transformOrigin: "center center",
            }}
          >
            <OmcorrSVG />
          </Box>
        </animated.div>
      );
    }
  )
);

const FloorPlan = () => {
  const { eventId } = useParams();
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState([0, 0]);
  const [stalls, setStalls] = useState([]);
  const [fromLabel, setFromLabel] = useState("");
  const [toLabel, setToLabel] = useState("");
  const [selectedBooth, setSelectedBooth] = useState("");
  const [notification, setNotification] = useState({
    show: false,
    message: "",
    severity: "info",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const navigatingRef = useRef(false);
  const isMobile = useMediaQuery("(max-width:600px)");
  const theme = useTheme();

  // Extract stalls from SVG when component mounts
  useEffect(() => {
    const extractStallsFromSVG = () => {
      // Define the actual stalls from the OmcorrSVG component
      const extractedStalls = [
        {
          id: "E413",
          name: "E 413 - FIRST PETROCHEMICAL",
          boothNumber: "E413",
          category: "Booths",
          transform: "translate(405.72 264.96)",
        },
        {
          id: "E108",
          name: "E 108 - TECH SOLUTIONS",
          boothNumber: "E108",
          category: "Booths",
          transform: "translate(368.88 231)",
        },
        {
          id: "E200",
          name: "E 200 - INNOVATION HUB",
          boothNumber: "E200",
          category: "Booths",
          transform: "translate(730.8 242.04)",
        },
        {
          id: "E501",
          name: "E 501 - DIGITAL SERVICES",
          boothNumber: "E501",
          category: "Booths",
          transform: "translate(543 847.8)",
        },
        {
          id: "ENTRANCE",
          name: "Main Entrance",
          boothNumber: "ENTRANCE",
          category: "Entrances",
          transform: "translate(396.6 525)",
        },
        {
          id: "COFFEE",
          name: "Coffee Station",
          boothNumber: "COFFEE",
          category: "Facilities",
          transform: "translate(380.52 542.52)",
        },
        {
          id: "REGISTRATION",
          name: "Registration Desk",
          boothNumber: "REG",
          category: "Facilities",
          transform: "translate(374.76 551.28)",
        },
        {
          id: "WELCOME",
          name: "Welcome Center",
          boothNumber: "WELCOME",
          category: "Facilities",
          transform: "translate(619.08 272.16)",
        },
        {
          id: "E300",
          name: "E 300 - INNOVATION LAB",
          boothNumber: "E300",
          category: "Booths",
          transform: "translate(740.04 576.96)",
        },
        {
          id: "E400",
          name: "E 400 - TECH HUB",
          boothNumber: "E400",
          category: "Booths",
          transform: "translate(543 871.44)",
        },
        {
          id: "E600",
          name: "E 600 - DIGITAL ZONE",
          boothNumber: "E600",
          category: "Booths",
          transform: "translate(543 918.72)",
        },
        {
          id: "E700",
          name: "E 700 - FUTURE TECH",
          boothNumber: "E700",
          category: "Booths",
          transform: "translate(543 895.08)",
        },
      ];

      setStalls(extractedStalls);
    };

    extractStallsFromSVG();
  }, []);

  // Handle booth selection
  const handleBoothSelect = (boothId) => {
    setSelectedBooth(boothId);
    handleNavigateToLabel(boothId);
    setNotification({
      show: true,
      message: `Navigating to ${
        stalls.find((s) => s.id === boothId)?.name || boothId
      }`,
      severity: "success",
    });
  };

  const handleResetView = () => {
    setScale(1);
    setOffset([0, 0]);
    setNotification({
      show: true,
      message: "View has been reset",
      severity: "success",
    });
  };

  const handleNavigateToLabel = useCallback(
    (label) => {
      if (!label || !svgRef.current || !containerRef.current) return;

      // Prevent multiple navigations happening simultaneously
      if (navigatingRef.current) return;
      navigatingRef.current = true;

      try {
        // Instead of trying to center on the specific label, simply reset the view first
        setScale(1);
        setOffset([0, 0]);

        // Allow a moment for the reset to apply
        setTimeout(() => {
          navigatingRef.current = false;
        }, 50);
      } catch (error) {
        console.error("Navigation error:", error);
        setNotification({
          show: true,
          message: `Error selecting ${label}`,
          severity: "error",
        });
        navigatingRef.current = false;
      }
    },
    [isMobile]
  );

  const handleCloseNotification = () => {
    setNotification({ ...notification, show: false });
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Typography>Loading floorplan...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          gap: 2,
        }}
      >
        <Typography variant="h6" color="error">
          Error Loading Floorplan
        </Typography>
        <Typography color="text.secondary">{error}</Typography>
        <Button variant="contained" onClick={() => window.location.reload()}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <>
      <Box
        ref={containerRef}
        sx={{
          position: "relative",
          overflow: "hidden",
          height: "100vh",
          touchAction: "none",
          cursor: "grab",
          "&:active": { cursor: "grabbing" },
        }}
      >
        {/* Zoom Controls */}
        <Box
          sx={{
            position: "absolute",
            top: 16,
            right: 16,
            zIndex: 10,
            display: "flex",
            flexDirection: "row",
            gap: 1,
            background: theme.palette.background.paper,
            p: 1,
            borderRadius: 2,
            boxShadow: 2,
          }}
        >
          <Tooltip title="Zoom In">
            <IconButton onClick={() => setScale((s) => Math.min(s + 0.2, 5))}>
              <Add />
            </IconButton>
          </Tooltip>

          <Tooltip title="Zoom Out">
            <IconButton onClick={() => setScale((s) => Math.max(s - 0.2, 0.5))}>
              <Remove />
            </IconButton>
          </Tooltip>

          <Tooltip title="Reset View">
            <IconButton onClick={handleResetView}>
              <GpsFixed />
            </IconButton>
          </Tooltip>

          <Tooltip title="Search Booths">
            <IconButton
              onClick={() => {
                console.log("Available stalls:", stalls);
              }}
            >
              <Search />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Booth Selection Dropdown */}
        <Box
          sx={{
            position: "absolute",
            top: 16,
            left: 16,
            zIndex: 10,
            background: theme.palette.background.paper,
            p: isMobile ? 1 : 1.5,
            borderRadius: 2,
            boxShadow: 2,
            minWidth: isMobile ? 200 : 250,
          }}
        >
          <FormControl fullWidth size={isMobile ? "small" : "medium"}>
            <InputLabel>Quick Select Booth</InputLabel>
            <Select
              value={selectedBooth}
              onChange={(e) => handleBoothSelect(e.target.value)}
              label="Quick Select Booth"
              startAdornment={
                <InputAdornment position="start">
                  <Flag color="primary" />
                </InputAdornment>
              }
            >
              {stalls.map((stall) => (
                <MenuItem key={stall.id} value={stall.id}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                      {stall.boothNumber}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {stall.name}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Location Legend */}
        <Box
          sx={{
            position: "absolute",
            top: isMobile ? 100 : 120,
            left: 16,
            zIndex: 10,
            background: theme.palette.background.paper,
            p: isMobile ? 1 : 1.5,
            borderRadius: 2,
            boxShadow: 2,
            display: "flex",
            flexDirection: "column",
            gap: 1,
            maxWidth: isMobile ? "50%" : "auto",
          }}
        >
          {fromLabel && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  background: "linear-gradient(45deg, #00e1b9, #39cbb3)",
                  boxShadow: "0 0 6px rgba(0, 225, 185, 0.7)",
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  fontWeight: "bold",
                  fontSize: isMobile ? "0.7rem" : "0.875rem",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                Location:{" "}
                <span style={{ color: "#00e1b9" }}>
                  {stalls.find((s) => s.id === fromLabel)?.name || fromLabel}
                </span>
              </Typography>
            </Box>
          )}

          {toLabel && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  background: "linear-gradient(45deg, #ff57b6, #ff89cf)",
                  boxShadow: "0 0 6px rgba(255, 87, 182, 0.7)",
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  fontWeight: "bold",
                  fontSize: isMobile ? "0.7rem" : "0.875rem",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                To:{" "}
                <span style={{ color: "#ff57b6" }}>
                  {stalls.find((s) => s.id === toLabel)?.name || toLabel}
                </span>
              </Typography>
            </Box>
          )}

          {!fromLabel && !toLabel && (
            <Typography
              variant="body2"
              sx={{
                fontStyle: "italic",
                color: "text.secondary",
                fontSize: isMobile ? "0.7rem" : "0.875rem",
              }}
            >
              Search for locations below
            </Typography>
          )}
        </Box>

        <SVGViewer
          scale={scale}
          setScale={setScale}
          offset={offset}
          setOffset={setOffset}
          fromLabel={fromLabel}
          toLabel={toLabel}
          containerRef={containerRef}
          stalls={stalls}
          ref={svgRef}
        />

        {/* Search Controls */}
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 10,
            background: theme.palette.background.paper,
            p: isMobile ? 2 : 3,
            borderRadius: "16px 16px 0 0",
            boxShadow: 3,
          }}
        >
          <Box display="grid" gap={1} gridTemplateColumns="1fr">
            {/* From Search */}
            <Autocomplete
              value={stalls.find((s) => s.id === fromLabel) || null}
              onChange={(event, newValue) => {
                if (newValue) {
                  setFromLabel(newValue.id);
                  handleNavigateToLabel(newValue.id);
                } else {
                  setFromLabel("");
                }
              }}
              options={stalls}
              getOptionLabel={(option) => option.name}
              groupBy={(option) => option.category}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Find Location"
                  fullWidth
                  size={isMobile ? "small" : "medium"}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocationOn color="success" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderColor: fromLabel ? "#00e1b9" : undefined,
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#00e1b9",
                        borderWidth: 2,
                      },
                    },
                  }}
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Typography variant="body1">{option.name}</Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ ml: 1 }}
                  >
                    {option.id}
                  </Typography>
                </Box>
              )}
              PaperComponent={(props) => (
                <Paper
                  {...props}
                  elevation={8}
                  sx={{
                    maxHeight: 300,
                    "&::-webkit-scrollbar": { width: 8 },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: "rgba(0,0,0,0.2)",
                      borderRadius: 4,
                    },
                  }}
                />
              )}
            />

            {/* To Search */}
            <Autocomplete
              value={stalls.find((s) => s.id === toLabel) || null}
              onChange={(event, newValue) => {
                if (newValue) {
                  setToLabel(newValue.id);
                  handleNavigateToLabel(newValue.id);
                } else {
                  setToLabel("");
                }
              }}
              options={stalls}
              getOptionLabel={(option) => option.name}
              groupBy={(option) => option.category}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="To"
                  fullWidth
                  size={isMobile ? "small" : "medium"}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <Flag color="error" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderColor: toLabel ? "#ff57b6" : undefined,
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#ff57b6",
                        borderWidth: 2,
                      },
                    },
                  }}
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Typography variant="body1">{option.name}</Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ ml: 1 }}
                  >
                    {option.id}
                  </Typography>
                </Box>
              )}
              PaperComponent={(props) => (
                <Paper
                  {...props}
                  elevation={8}
                  sx={{
                    maxHeight: 300,
                    "&::-webkit-scrollbar": { width: 8 },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: "rgba(0,0,0,0.2)",
                      borderRadius: 4,
                    },
                  }}
                />
              )}
            />
          </Box>

          {/* Clear button */}
          <Button
            fullWidth
            variant="outlined"
            size={isMobile ? "small" : "medium"}
            sx={{
              mt: 2,
              fontWeight: "bold",
              py: isMobile ? 0.5 : 1,
            }}
            onClick={() => {
              setFromLabel("");
              setToLabel("");
              setNotification({
                show: true,
                message: "Selections cleared",
                severity: "info",
              });
            }}
          >
            Clear Selections
          </Button>
        </Box>

        {/* Notification */}
        <Snackbar
          open={notification.show}
          autoHideDuration={3000}
          onClose={handleCloseNotification}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            onClose={handleCloseNotification}
            severity={notification.severity}
            sx={{ width: "100%" }}
          >
            {notification.message}
          </Alert>
        </Snackbar>
      </Box>
    </>
  );
};

export default React.memo(FloorPlan);
